/**
 * Selector hooks against the global store. Components consume these — not the
 * store directly — so the binding contract for Phase-3 agents is stable.
 *
 * Selectors are passed to `useSyncExternalStore`'s server-snapshot slot so SSR
 * (and React-strict-mode renders) get the same value as the first client tick.
 */
import { useSyncExternalStore } from "react";
import type { AppState } from "./store.js";
import { store } from "./store.js";

function identity<T>(x: T): T {
  return x;
}

export function useAppState(): AppState {
  return useSyncExternalStore(store.subscribe, store.getSnapshot, store.getSnapshot);
}

export function useSelector<T>(selector: (s: AppState) => T): T {
  const select = (): T => selector(store.getSnapshot());
  return useSyncExternalStore(store.subscribe, select, select);
}

export const useConnection = () => useSelector((s) => s.connection);
export const useRoomCode = () => useSelector((s) => s.code);
export const useDisplayName = () => useSelector((s) => s.name);
export const useLobby = () => useSelector((s) => s.lobby);
export const useView = () => useSelector((s) => s.view);
export const useYou = () => useSelector((s) => s.you);
export const useReclaimToken = () => useSelector((s) => s.reclaimToken);
export const useRecentEvents = () => useSelector((s) => s.recentEvents);
export const useErrors = () => useSelector((s) => s.errors);

// Re-export identity so an agent importing this file doesn't pull two paths.
export { identity };
